import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Wallet, Trash2, Building2, ArrowRightLeft, Edit2, Settings2, ReceiptText, FileText, Upload, Search } from "lucide-react";
import { useFinanceStore } from "../stores/finance-store";
import { cn, formatCurrency, formatDate, normalizeCategoryName, parseCurrency, roundMoney } from "../lib/utils";
import { Checkbox } from "../components/ui/checkbox";
import { CATEGORY_TYPES } from "../lib/transaction-types";
import { BankIcon } from "../components/BankIcon";
import { BankIconPicker } from "../components/BankIconPicker";
import { CurrencyInput } from "../components/CurrencyInput";
import { CurrencySelect } from "../components/CurrencySelect";
import { TransactionCombobox } from "../components/TransactionCombobox";
import { getSignedTransactionAmount } from "../lib/finance-calculations";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { useAccountBalances, useAccountFormDialog, useAccountTransferDialog, useBalanceAdjustmentDialog, useBalanceAdjustmentWizard, useStatementImportDialog } from "../hooks/useAccountsPage";

const ACCOUNT_TYPES = [
  { value: "checking", label: "Conta Corrente" },
  { value: "savings", label: "Poupança" },
  { value: "investment", label: "Investimento" },
  { value: "cash", label: "Dinheiro" },
  { value: "other", label: "Outro" },
];

const ACCOUNT_NATURES = [
  { value: "D", label: "Devedor" },
  { value: "C", label: "Credor" },
];

const getAccountTypeLabel = (type) => ACCOUNT_TYPES.find((item) => item.value === type)?.label || type;
const getAccountNatureLabel = (nature) => ACCOUNT_NATURES.find((item) => item.value === nature)?.label || "Devedor";

export default function AccountsPage() {
  const navigate = useNavigate();
  const { accounts, transactions, fetchAccounts, fetchCategories, fetchTransactions, deleteAccount, isLoading } = useFinanceStore();
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchAccounts();
    fetchCategories();
    fetchTransactions();
  }, [fetchAccounts, fetchCategories, fetchTransactions]);

  const { totalBalance, getAccountBalance, accountBalanceMonth, accountBalanceYear } = useAccountBalances(accounts, transactions);
  const adjustmentDialog = useBalanceAdjustmentDialog();
  const formDialog = useAccountFormDialog(accountBalanceMonth, accountBalanceYear, adjustmentDialog.createHiddenBalanceAdjustment, getAccountBalance);
  const transferDialog = useAccountTransferDialog();
  const statementImport = useStatementImportDialog();

  const handleDelete = async () => {
    if (!deleteId) return;

    const result = await deleteAccount(deleteId);
    if (result.success) {
      toast.success("Conta excluída!");
    } else {
      toast.error("Erro ao excluir conta");
    }
    setDeleteId(null);
  };

  const openAccountStatement = (account) => {
    if (!account?.id) return;
    navigate(`/account-statement?account_id=${encodeURIComponent(account.id)}`);
  };

  return (
    <div className="space-y-6 pb-44 sm:pb-0">
      <AccountsPageHeader
        onTransfer={() => transferDialog.setShowTransferDialog(true)}
        onAddAccount={formDialog.openCreateAccountDialog}
      />

      <TotalBalanceCard totalBalance={totalBalance} />

      <AccountsGrid
        accounts={accounts}
        isLoading={isLoading}
        getAccountBalance={getAccountBalance}
        onOpenStatement={openAccountStatement}
        onOpenAdjustment={adjustmentDialog.handleOpenAdjustment}
        onOpenEdit={formDialog.handleOpenEdit}
        onDelete={setDeleteId}
        onAddAccount={formDialog.openCreateAccountDialog}
        onOpenStatementImport={statementImport.handleOpenStatementImport}
      />

      <MobileAccountActions
        onTransfer={() => transferDialog.setShowTransferDialog(true)}
        onAddAccount={formDialog.openCreateAccountDialog}
      />

      <AddAccountDialog
        open={formDialog.showAddDialog}
        onOpenChange={formDialog.setShowAddDialog}
        handleSubmit={formDialog.handleSubmit}
        formData={formDialog.formData}
        setFormData={formDialog.setFormData}
        editId={formDialog.editId}
      />
      <TransferDialog
        open={transferDialog.showTransferDialog}
        onOpenChange={transferDialog.setShowTransferDialog}
        handleTransferSubmit={transferDialog.handleTransferSubmit}
        transferData={transferDialog.transferData}
        setTransferData={transferDialog.setTransferData}
        accounts={accounts}
        categories={transferDialog.categories}
      />
      <BalanceAdjustmentWizardDialog
        open={adjustmentDialog.showAdjustmentDialog}
        onOpenChange={adjustmentDialog.handleAdjustmentOpenChange}
        account={adjustmentDialog.adjustmentAccount}
        accounts={accounts}
        transactions={transactions}
        currentBalance={adjustmentDialog.adjustmentAccount ? getAccountBalance(adjustmentDialog.adjustmentAccount) : 0}
        onCreateAdjustment={adjustmentDialog.createHiddenBalanceAdjustment}
      />
      <StatementImportDialog
        open={statementImport.showStatementImportDialog}
        onOpenChange={statementImport.handleStatementImportOpenChange}
        account={statementImport.statementImportAccount}
        existingTransactions={statementImport.statementAccountTransactions}
        onParse={statementImport.parseAccountStatementFile}
        onImport={statementImport.handleImportStatementItems}
      />
      <DeleteAccountDialog
        deleteId={deleteId}
        setDeleteId={setDeleteId}
        handleDelete={handleDelete}
      />
    </div>
  );
}

function AccountsPageHeader({ onTransfer, onAddAccount }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Contas</h1>
        <p className="text-muted-foreground mt-1">
          Gerencie suas contas bancárias e carteiras
        </p>
      </div>

      <div className="hidden gap-2 sm:flex">
        <Button variant="outline" onClick={onTransfer}>
          <ArrowRightLeft className="w-4 h-4 mr-2" />
          Transferência
        </Button>
        <Button onClick={onAddAccount} className="btn-glow" data-testid="add-account-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Conta
        </Button>
      </div>
    </div>
  );
}

function TotalBalanceCard({ totalBalance }) {
  return (
    <Card className="balance-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Wallet className="w-4 h-4" />
          Patrimônio Total
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-bold font-mono">
          {formatCurrency(totalBalance)}
        </p>
      </CardContent>
    </Card>
  );
}

function MobileAccountActions({ onTransfer, onAddAccount }) {
  return (
    <div className="fixed bottom-20 left-1/2 z-40 grid w-[calc(100vw-2rem)] max-w-[340px] -translate-x-1/2 grid-cols-2 gap-2 rounded-xl border bg-card p-3 shadow-lg sm:hidden">
      <Button variant="outline" onClick={onTransfer}>
        <ArrowRightLeft className="w-4 h-4 mr-2" />
        Transferência
      </Button>
      <Button onClick={onAddAccount} className="btn-glow" data-testid="add-account-btn-mobile">
        <Plus className="w-4 h-4 mr-2" />
        Nova Conta
      </Button>
    </div>
  );
}

function AccountsGrid({ accounts, isLoading, getAccountBalance, onOpenStatement, onOpenAdjustment, onOpenEdit, onDelete, onAddAccount, onOpenStatementImport }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-40" />
        ))}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <Card>
        <CardContent className="empty-state py-12">
          <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">Nenhuma conta cadastrada</p>
          <Button onClick={onAddAccount}>
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Conta
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {accounts.map((account) => (
        <AccountCard
          key={account.id}
          account={account}
          balance={getAccountBalance(account)}
          onOpenStatement={onOpenStatement}
          onOpenAdjustment={onOpenAdjustment}
          onOpenEdit={onOpenEdit}
          onOpenStatementImport={onOpenStatementImport}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function AccountCard({ account, balance, onOpenStatement, onOpenAdjustment, onOpenEdit, onDelete, onOpenStatementImport }) {
  return (
    <Card className="card-hover">
      <CardContent className="flex flex-col gap-4 p-4 sm:p-5">
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3">
          <BankIcon iconId={account.bankIconId} bankName={account.bank} size="lg" />
          <div className="min-w-0 text-right">
            <h3 className="truncate font-semibold">{account.name}</h3>
            <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
              <Badge variant="outline" className="text-[11px] font-medium">
                {getAccountTypeLabel(account.type)}
              </Badge>
              <Badge variant="secondary" className="text-[11px] font-medium">
                {getAccountNatureLabel(account.nature)}
              </Badge>
              <Badge variant="outline" className="text-[11px] font-medium">
                {account.currency || "BRL"}
              </Badge>
              <Badge variant={account.active !== false ? "secondary" : "destructive"} className="text-[11px] font-medium">
                {account.active !== false ? "Ativa" : "Inativa"}
              </Badge>
              {account.showOnHome !== false && (
                <Badge variant="outline" className="text-[11px] font-medium">
                  Home
                </Badge>
              )}
              {account.showOnDashboard !== false && (
                <Badge variant="outline" className="text-[11px] font-medium">
                  Dashboard
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="font-mono text-3xl font-bold tracking-tight">
            {formatCurrency(balance, account.currency || "BRL")}
          </p>
        </div>

        <div className="grid grid-cols-5 gap-1.5 border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            className="h-11 flex-col gap-0.5 px-1 text-xs text-muted-foreground hover:text-primary"
            onClick={() => onOpenStatement(account)}
            title="Extrato"
          >
            <ReceiptText className="h-4 w-4" />
            <span>Extrato</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-11 flex-col gap-0.5 px-1 text-xs text-muted-foreground hover:text-primary"
            onClick={() => onOpenStatementImport(account)}
            title="Importar extrato (OFX ou PDF)"
            data-testid="statement-import-btn"
          >
            <Upload className="h-4 w-4" />
            <span>Importar</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-11 flex-col gap-0.5 px-1 text-xs text-muted-foreground hover:text-primary"
            onClick={() => onOpenAdjustment(account)}
            title="Ajustar saldo"
          >
            <Settings2 className="h-4 w-4" />
            <span>Ajustar</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-11 flex-col gap-0.5 px-1 text-xs text-muted-foreground hover:text-primary"
            onClick={() => onOpenEdit(account)}
            title="Editar"
          >
            <Edit2 className="h-4 w-4" />
            <span>Editar</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-11 flex-col gap-0.5 px-1 text-xs text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(account.id)}
            title="Excluir"
          >
            <Trash2 className="h-4 w-4" />
            <span>Excluir</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AddAccountDialog({ open, onOpenChange, handleSubmit, formData, setFormData, editId }) {
  const handleBankIconSelect = (bankIconId, icon) => {
    setFormData((current) => ({
      ...current,
      bankIconId,
      bank: current.bank?.trim() ? current.bank : icon.label,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-4 overflow-hidden p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{editId ? "Editar Conta" : "Nova Conta"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label>Nome da Conta</Label>
            <Input placeholder="Ex: Nubank, Carteira..." value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value.toLocaleUpperCase("pt-BR") })} required />
          </div>
          <div className="space-y-2">
            <Label>Tipo de Conta</Label>
            <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACCOUNT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Natureza</Label>
            <Select value={formData.nature} onValueChange={(value) => setFormData({ ...formData, nature: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACCOUNT_NATURES.map((nature) => (
                  <SelectItem key={nature.value} value={nature.value}>
                    {nature.value} - {nature.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Banco (opcional)</Label>
            <Input placeholder="Ex: Nubank, Itaú..." value={formData.bank} onChange={(e) => setFormData({ ...formData, bank: e.target.value.toLocaleUpperCase("pt-BR") })} />
          </div>
          <BankIconPicker
            value={formData.bankIconId}
            bankName={formData.bank}
            onSelect={handleBankIconSelect}
          />
          <div className="space-y-2">
            <Label>Moeda</Label>
            <CurrencySelect value={formData.currency || "BRL"} onValueChange={(value) => setFormData({ ...formData, currency: value })} />
          </div>
          <div className="space-y-2">
            <Label>{editId ? "Saldo atual" : "Saldo inicial"}</Label>
            <CurrencyInput
              placeholder="0,00"
              currency={formData.currency || "BRL"}
              value={formData.initialBalance}
              onChange={(value) => setFormData({ ...formData, initialBalance: value })}
            />
          </div>
          <div className="space-y-3 rounded-lg border p-3">
            <Label>Visibilidade</Label>
            <div className="flex items-center justify-between gap-4">
              <Label className="text-sm font-medium">Conta ativa</Label>
              <Switch
                checked={formData.active !== false}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <Label className="text-sm font-medium">Visível na Home</Label>
              <Switch
                checked={formData.showOnHome !== false}
                onCheckedChange={(checked) => setFormData({ ...formData, showOnHome: checked })}
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <Label className="text-sm font-medium">Visível no Dashboard</Label>
              <Switch
                checked={formData.showOnDashboard !== false}
                onCheckedChange={(checked) => setFormData({ ...formData, showOnDashboard: checked })}
              />
            </div>
          </div>
          <div className="sticky bottom-0 z-10 flex gap-2 bg-background py-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="flex-1 btn-glow">{editId ? "Atualizar" : "Adicionar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ open, onOpenChange, handleTransferSubmit, transferData, setTransferData, accounts, categories }) {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const transferCategories = categories.filter((category) => category.type === CATEGORY_TYPES.TRANSFER);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transferência entre Contas</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleTransferSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>De</Label>
            <Select value={transferData.fromAccountId} onValueChange={(val) => setTransferData({ ...transferData, fromAccountId: val })}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {accounts.map(acc => <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Para</Label>
            <Select value={transferData.toAccountId} onValueChange={(val) => setTransferData({ ...transferData, toAccountId: val })}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                {accounts.map(acc => <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Valor</Label>
            <CurrencyInput placeholder="0,00" value={transferData.amount} onChange={(value) => setTransferData({ ...transferData, amount: value })} />
          </div>
          <TransactionCombobox
            label="Categoria (opcional)"
            value={transferData.category}
            setValue={(value) => setTransferData((current) => ({ ...current, category: value }))}
            selectedId={transferData.categoryId}
            setSelectedId={(value) => setTransferData((current) => ({ ...current, categoryId: value }))}
            items={transferCategories}
            open={categoryOpen}
            setOpen={setCategoryOpen}
            placeholder="Selecione ou digite uma categoria"
            testId="account-transfer-category"
            showColor={true}
          />
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="flex-1 btn-glow">Transferir</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentWizardStepIndicator({ step, stepLabels }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {stepLabels.map((label, index) => {
        const itemStep = index + 1;
        const isActive = step === itemStep;
        const isDone = step > itemStep;

        return (
          <div
            key={label}
            className={`rounded-md border px-2 py-2 text-center text-xs font-semibold ${isActive ? "border-primary bg-primary/10 text-primary" : isDone ? "border-primary/40 text-primary" : "text-muted-foreground"}`}
          >
            {itemStep} {label}
          </div>
        );
      })}
    </div>
  );
}

function AdjustmentTypeStep({ adjustmentType, setAdjustmentType, monthName }) {
  return (
    <div className="space-y-3">
      <Label>Selecionar tipo de ajuste</Label>
      <button
        type="button"
        className={`w-full rounded-lg border p-4 text-left transition-colors ${adjustmentType === "balance" ? "border-primary bg-primary/10" : "hover:bg-muted/50"}`}
        onClick={() => setAdjustmentType("balance")}
      >
        <span className="block font-semibold">Ajuste de Saldo</span>
        <span className="mt-1 block text-sm text-muted-foreground">
          Cria um ajuste técnico hoje para igualar o saldo atual da conta.
        </span>
      </button>
      <button
        type="button"
        className={`w-full rounded-lg border p-4 text-left transition-colors ${adjustmentType === "opening" ? "border-primary bg-primary/10" : "hover:bg-muted/50"}`}
        onClick={() => setAdjustmentType("opening")}
      >
        <span className="block font-semibold">Ajuste Saldo Inicial</span>
        <span className="mt-1 block text-sm text-muted-foreground">
          Recalcula o saldo inicial de {monthName} a partir do saldo informado.
        </span>
      </button>
    </div>
  );
}

function AdjustmentBalanceStep({ currentBalance, currency, targetBalance, setTargetBalance, targetBalanceInputRef }) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border bg-muted/40 p-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Saldo calculado agora</span>
          <span className="font-mono font-semibold">{formatCurrency(currentBalance, currency)}</span>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Qual saldo atual da conta?</Label>
        <CurrencyInput
          placeholder="0,00"
          currency={currency}
          value={targetBalance}
          onChange={setTargetBalance}
          customRef={targetBalanceInputRef}
          autoFocus
        />
      </div>
    </div>
  );
}

function ReconciledTransactionsList({ transactions, currency }) {
  return (
    <div className="space-y-2 rounded-md bg-muted/40 p-2">
      {transactions.length > 0 ? (
        transactions.map((tx) => {
          const signedAmount = getSignedTransactionAmount(tx);
          const amountColor = signedAmount >= 0 ? "text-success" : "text-destructive";

          return (
            <div key={tx.id || `${tx.description}-${tx.date}`} className="flex items-start justify-between gap-3 text-xs">
              <div className="min-w-0">
                <div className="truncate font-medium">{tx.description || "Sem descrição"}</div>
                <div className="text-muted-foreground">{formatDate(tx.date)}</div>
              </div>
              <span className={`shrink-0 font-mono font-semibold ${amountColor}`}>
                {formatCurrency(signedAmount, currency)}
              </span>
            </div>
          );
        })
      ) : (
        <div className="text-xs text-muted-foreground">Nenhum lançamento conciliado no mês.</div>
      )}
    </div>
  );
}

function AdjustmentConfirmationStep({
  isInitialAdjustment,
  adjustmentSummary,
  currency,
  primaryAdjustmentAmount,
  primaryAmountColor,
  diffColor,
  monthName,
  showReconciledItems,
  setShowReconciledItems,
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/40 p-4 text-center">
        <p className="text-sm text-muted-foreground">
          {isInitialAdjustment
            ? "Vou definir o saldo inicial como"
            : `Vou lançar no dia Hoje (${formatDate(adjustmentSummary.adjustmentDate)})`}
        </p>
        <p className={`mt-2 font-mono text-2xl font-bold ${primaryAmountColor}`}>
          {formatCurrency(primaryAdjustmentAmount, currency)}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {isInitialAdjustment
            ? `Data técnica: ${formatDate(adjustmentSummary.adjustmentDate)}`
            : `Saldo final pretendido: ${formatCurrency(adjustmentSummary.targetBalance, currency)}`}
        </p>
      </div>

      {isInitialAdjustment && (
        <div className="space-y-2 rounded-lg border p-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Saldo inicial atual</span>
            <span className="font-mono font-semibold">{formatCurrency(adjustmentSummary.openingBalance, currency)}</span>
          </div>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 rounded-md text-left transition-colors hover:bg-muted/50"
            onClick={() => setShowReconciledItems((current) => !current)}
            aria-expanded={showReconciledItems}
          >
            <span className="text-muted-foreground">
              Conciliado em {monthName} <span className="text-xs">({adjustmentSummary.reconciledTransactions.length})</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="font-mono font-semibold">{formatCurrency(adjustmentSummary.reconciledMonthTotal, currency)}</span>
              <span className="text-xs text-muted-foreground">{showReconciledItems ? "▲" : "▼"}</span>
            </span>
          </button>
          {showReconciledItems && (
            <ReconciledTransactionsList transactions={adjustmentSummary.reconciledTransactions} currency={currency} />
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Saldo inicial pretendido</span>
            <span className="font-mono font-semibold">{formatCurrency(adjustmentSummary.targetOpeningBalance, currency)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 border-t pt-2">
            <span className="font-semibold">Ajuste técnico</span>
            <span className={`font-mono font-bold ${primaryAmountColor}`}>{formatCurrency(adjustmentSummary.targetOpeningBalance, currency)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>Correção interna sobre o saldo atual</span>
            <span className={`font-mono font-semibold ${diffColor}`}>{formatCurrency(adjustmentSummary.diff, currency)}</span>
          </div>
        </div>
      )}

      <p className="text-center text-sm text-muted-foreground">
        {isInitialAdjustment
          ? "Confirma definir este saldo inicial para a conta?"
          : "Confirma a criação deste lançamento técnico?"}
      </p>
    </div>
  );
}

function AdjustmentWizardFooter({ step, isSubmitting, onBack, onCancel, onNext, onConfirm }) {
  return (
    <div className="flex gap-2 pt-2">
      {step > 1 ? (
        <Button type="button" variant="outline" className="flex-1" onClick={onBack} disabled={isSubmitting}>
          Voltar
        </Button>
      ) : (
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
      )}
      {step < 3 ? (
        <Button type="button" className="flex-1 btn-glow" onClick={onNext}>
          Continuar
        </Button>
      ) : (
        <Button type="button" className="flex-1 btn-glow" onClick={onConfirm} disabled={isSubmitting}>
          {isSubmitting ? "Confirmando..." : "Confirmar"}
        </Button>
      )}
    </div>
  );
}

function BalanceAdjustmentWizardDialog({
  open,
  onOpenChange,
  account,
  accounts = [],
  transactions,
  currentBalance,
  onCreateAdjustment,
}) {
  const wizard = useBalanceAdjustmentWizard(open, account, accounts, transactions, currentBalance, onCreateAdjustment);
  const closeDialog = () => onOpenChange(false);
  const stepLabels = ["Tipo", "Saldo", "Confirmação"];
  const primaryAdjustmentAmount = wizard.isInitialAdjustment
    ? wizard.adjustmentSummary?.targetOpeningBalance
    : wizard.adjustmentSummary?.diff;
  const primaryAmountColor = (primaryAdjustmentAmount || 0) >= 0 ? "text-success" : "text-destructive";
  const diffColor = (wizard.adjustmentSummary?.diff || 0) >= 0 ? "text-success" : "text-destructive";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajustar saldo</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {account?.name ? `Conta: ${account.name}` : "Selecione como deseja ajustar o saldo."}
          </p>
        </DialogHeader>

        <div className="space-y-5">
          <AdjustmentWizardStepIndicator step={wizard.step} stepLabels={stepLabels} />

          {wizard.step === 1 && (
            <AdjustmentTypeStep adjustmentType={wizard.adjustmentType} setAdjustmentType={wizard.setAdjustmentType} monthName={wizard.monthName} />
          )}

          {wizard.step === 2 && (
            <AdjustmentBalanceStep
              currentBalance={currentBalance}
              currency={wizard.currency}
              targetBalance={wizard.targetBalance}
              setTargetBalance={wizard.setTargetBalance}
              targetBalanceInputRef={wizard.targetBalanceInputRef}
            />
          )}

          {wizard.step === 3 && wizard.adjustmentSummary && (
            <AdjustmentConfirmationStep
              isInitialAdjustment={wizard.isInitialAdjustment}
              adjustmentSummary={wizard.adjustmentSummary}
              currency={wizard.currency}
              primaryAdjustmentAmount={primaryAdjustmentAmount}
              primaryAmountColor={primaryAmountColor}
              diffColor={diffColor}
              monthName={wizard.monthName}
              showReconciledItems={wizard.showReconciledItems}
              setShowReconciledItems={wizard.setShowReconciledItems}
            />
          )}

          <AdjustmentWizardFooter
            step={wizard.step}
            isSubmitting={wizard.isSubmitting}
            onBack={wizard.handleBack}
            onCancel={closeDialog}
            onNext={wizard.handleNext}
            onConfirm={() => wizard.handleConfirm(closeDialog)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function normalizeStatementImportText(value) {
  return normalizeCategoryName(value).replace(/[^A-Z0-9]/g, "");
}

function getStatementImportDateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function isProbableStatementDuplicate(item, existingTransactions = []) {
  if (item.fitid && existingTransactions.some((tx) => tx.statement_fitid && tx.statement_fitid === item.fitid)) {
    return true;
  }

  const itemDate = getStatementImportDateKey(`${item.date}T12:00:00`);
  const itemAmount = roundMoney(parseCurrency(item.amount));
  const itemDescription = normalizeStatementImportText(item.description);
  const itemType = item.direction === "credit" ? "income" : "expense";

  return existingTransactions.some((tx) => {
    const txDate = getStatementImportDateKey(tx.date);
    const txAmount = roundMoney(parseCurrency(tx.amount));
    const txDescription = normalizeStatementImportText(tx.description);

    return txDate === itemDate &&
      txAmount === itemAmount &&
      tx.type === itemType &&
      (
        txDescription === itemDescription ||
        txDescription.includes(itemDescription) ||
        itemDescription.includes(txDescription)
      );
  });
}

function getStatementAmountLabel(value, direction = "debit") {
  const prefix = direction === "credit" ? "+" : "-";
  return `${prefix} ${formatCurrency(Math.abs(parseCurrency(value)))}`;
}

function getStatementAmountClass(direction = "debit") {
  return direction === "credit" ? "text-emerald-600" : "text-destructive";
}

function getSignedStatementAmount(item = {}) {
  const amount = Math.abs(parseCurrency(item.amount));
  return item.direction === "credit" ? amount : -amount;
}

function StatementImportDialog({ open, onOpenChange, account, existingTransactions = [], onParse, onImport }) {
  const [file, setFile] = useState(null);
  const [items, setItems] = useState([]);
  const [directionOverrides, setDirectionOverrides] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const enrichedItems = useMemo(() => (
    items.map((item, index) => {
      const id = `${item.date}-${item.amount}-${index}`;
      const directedItem = {
        ...item,
        id,
        direction: directionOverrides[id] || item.direction || "debit",
      };

      return {
        ...directedItem,
        duplicate: isProbableStatementDuplicate(directedItem, existingTransactions),
      };
    })
  ), [existingTransactions, items, directionOverrides]);

  const selectedItems = enrichedItems.filter((item) => selectedIds.has(item.id));
  const duplicateCount = enrichedItems.filter((item) => item.duplicate).length;
  const selectedTotal = selectedItems.reduce((sum, item) => sum + getSignedStatementAmount(item), 0);

  const reset = () => {
    setFile(null);
    setItems([]);
    setDirectionOverrides({});
    setSelectedIds(new Set());
    setIsParsing(false);
    setIsImporting(false);
  };

  const handleOpenChange = (nextOpen) => {
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setItems([]);
    setDirectionOverrides({});
    setSelectedIds(new Set());
  };

  const handleParse = async () => {
    if (!file) {
      toast.error("Selecione um arquivo OFX ou PDF de extrato.");
      return;
    }

    setIsParsing(true);
    const result = await onParse(file);
    setIsParsing(false);

    if (!result.success) {
      toast.error(result.error || "Erro ao ler extrato.");
      return;
    }

    const nextItems = result.items || [];
    setItems(nextItems);
    setDirectionOverrides({});
    const nextSelected = new Set();
    nextItems.forEach((item, index) => {
      const id = `${item.date}-${item.amount}-${index}`;
      const directedItem = { ...item, direction: item.direction || "debit" };
      if (!isProbableStatementDuplicate(directedItem, existingTransactions)) {
        nextSelected.add(id);
      }
    });
    setSelectedIds(nextSelected);
  };

  const toggleItem = (id, checked) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const toggleItemDirection = (item) => {
    setDirectionOverrides((current) => ({
      ...current,
      [item.id]: item.direction === "credit" ? "debit" : "credit",
    }));
  };

  const handleImport = async () => {
    if (!selectedItems.length) {
      toast.error("Selecione ao menos uma transação para importar.");
      return;
    }

    setIsImporting(true);
    const result = await onImport(selectedItems);
    setIsImporting(false);

    if (result.success) {
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden p-4 sm:max-w-3xl sm:p-6">
        <DialogHeader>
          <DialogTitle>Importar extrato</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border p-4">
            <Label htmlFor="statement-import-file">Arquivo OFX ou PDF</Label>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Input
                id="statement-import-file"
                type="file"
                accept=".ofx,application/x-ofx,application/pdf,.pdf"
                onChange={handleFileChange}
                data-testid="statement-import-file"
              />
              <Button type="button" onClick={handleParse} disabled={!file || isParsing}>
                <FileText className="mr-2 h-4 w-4" />
                {isParsing ? "Lendo..." : "Ler arquivo"}
              </Button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              As transações serão importadas para a conta <span className="font-medium">{account?.name}</span>.
              Créditos entram como receita e débitos como despesa — toque no valor para inverter.
            </p>
          </div>

          {enrichedItems.length > 0 && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <StatementSummaryItem icon={ReceiptText} label="Encontradas" value={String(enrichedItems.length)} />
                <StatementSummaryItem icon={Search} label="Duplicadas" value={String(duplicateCount)} />
                <StatementSummaryItem icon={Wallet} label="Selecionado" value={formatCurrency(selectedTotal)} />
              </div>

              <div className="overflow-hidden rounded-lg border">
                <div className="grid grid-cols-[36px_92px_minmax(0,1fr)_110px] gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-semibold text-muted-foreground">
                  <span />
                  <span>Data</span>
                  <span>Descrição</span>
                  <span className="text-right">Valor</span>
                </div>
                <div className="max-h-[320px] overflow-y-auto">
                  {enrichedItems.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        "grid grid-cols-[36px_92px_minmax(0,1fr)_110px] items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0",
                        item.duplicate && "bg-muted/30 text-muted-foreground"
                      )}
                    >
                      <Checkbox
                        checked={selectedIds.has(item.id)}
                        onCheckedChange={(checked) => toggleItem(item.id, checked === true)}
                      />
                      <span>{formatDate(`${item.date}T12:00:00`)}</span>
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{item.description}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {item.source_layout?.toUpperCase()}
                          {item.duplicate ? " • duplicado provável" : ""}
                          {item.warnings?.length ? ` • ${item.warnings[0]}` : ""}
                        </span>
                      </span>
                      <button
                        type="button"
                        className={cn(
                          "whitespace-nowrap rounded px-1 py-0.5 text-right font-mono font-bold tabular-nums transition-colors hover:bg-muted",
                          getStatementAmountClass(item.direction)
                        )}
                        onClick={() => toggleItemDirection(item)}
                        title="Alternar receita/despesa"
                        aria-label={`Alternar tipo de ${item.description}`}
                      >
                        {getStatementAmountLabel(item.amount, item.direction)}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="button" className="flex-1 btn-glow" onClick={handleImport} disabled={!selectedItems.length || isImporting}>
              <Upload className="mr-2 h-4 w-4" />
              {isImporting ? "Importando..." : "Importar transações"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatementSummaryItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border bg-card p-3">
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="mt-1 font-mono text-lg font-bold">{value}</p>
      </div>
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  );
}

function DeleteAccountDialog({ deleteId, setDeleteId, handleDelete }) {
  return (
    <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir conta?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta ação não pode ser desfeita. A conta será removida, mas as transações associadas serão mantidas.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Excluir</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
